<template>
    <transition name="fade">
        <div
            v-if="visible"
            class="governance-overlay"
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="governance-overlay-title"
            tabindex="-1"
            ref="overlay"
        >
            <div class="governance-overlay__backdrop" />
            <div class="governance-overlay__panel">
                <div class="governance-overlay__topbar" />

                <div class="governance-overlay__icon-wrap">
                    <div class="governance-overlay__shield">
                        <v-icon color="error" size="44">mdi-shield-alert-outline</v-icon>
                    </div>
                    <span class="governance-overlay__lock-badge">
                        <v-icon color="white" size="14">mdi-lock</v-icon>
                    </span>
                </div>

                <h2 id="governance-overlay-title" class="governance-overlay__title">
                    Acesso Bloqueado
                </h2>

                <p class="governance-overlay__subtitle">
                    <strong>Atenção:</strong> seu grupo de gestão atingiu o limite
                    de notificações para
                    <strong v-if="rooms.length === 1">a sala "{{ rooms[0].name || `#${rooms[0].id}` }}"</strong>
                    <strong v-else>{{ rooms.length }} salas listadas abaixo</strong>.
                    Resolva a pendência para liberar os demais módulos.
                </p>
                <p class="governance-overlay__subtitle governance-overlay__headline">
                    {{ headlineText }}.
                </p>

                <ul class="governance-overlay__rooms">
                    <li
                        v-for="room in rooms"
                        :key="room.id"
                        class="governance-overlay__room"
                    >
                        <div class="governance-overlay__room-icon">
                            <v-icon small color="#b91c1c">mdi-lock-outline</v-icon>
                        </div>
                        <div class="governance-overlay__room-info">
                            <div class="governance-overlay__room-name">
                                {{ room.name || `Sala #${room.id}` }}
                            </div>
                            <div class="governance-overlay__room-meta">
                                <span v-if="room.days_in_red != null">
                                    {{ room.days_in_red }} dia(s) sem ação
                                </span>
                            </div>
                        </div>
                        <span class="governance-overlay__room-chip">
                            {{ room.students || 0 }}/{{ room.capacity || 0 }}
                            <strong v-if="room.percent">({{ Math.round(room.percent) }}%)</strong>
                        </span>
                    </li>
                </ul>

                <div class="governance-overlay__hint">
                    <v-icon small color="#475569" class="mr-2">mdi-refresh</v-icon>
                    O acesso será restaurado automaticamente assim que todas as
                    salas acima deixarem o estado Vermelho — seja por realocação
                    de turma ou ajuste de capacidade.
                </div>

                <v-btn
                    color="error"
                    block
                    large
                    depressed
                    class="governance-overlay__cta"
                    @click="goToVacancyControl"
                >
                    <v-icon left>mdi-arrow-right-bold</v-icon>
                    {{ ctaLabel }}
                </v-btn>

                <v-btn
                    v-if="isOnVacancyControl"
                    text
                    small
                    class="governance-overlay__dismiss"
                    @click="dismiss"
                >
                    Continuar nesta tela
                </v-btn>
            </div>
        </div>
    </transition>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import { eventBus } from '../../../components/js/eventBus.js'

export default {
    name: 'OccupancyBlockOverlay',

    data() {
        return {
            dismissedOnRoute: null,
        }
    },

    computed: {
        ...mapGetters('governance', [
            'isBlocked',
            'blockingRooms',
            'totalCriticalSlots',
        ]),
        ...mapGetters('auth', ['hasAcesso', 'hasToken']),
        // Governança de ocupação é uma feature de operador. Numa sessão de
        // professor (magic link / SSO docente) o overlay não deve sequer
        // consultar o block-state: o endpoint roda no guard `api`/`users` e
        // devolveria erro pra um token `api-teacher`, derrubando o painel.
        isTeacherSession() {
            if (['login/teacher', 'teacher/page', 'teacher-panel', 'availability-teacher']
                .includes(this.$route?.name)) {
                return true
            }
            const token = this.$store?.state?.auth?.token
            if (!token || typeof token !== 'string') return false
            try {
                const part = token.split('.')[1]
                if (!part) return false
                const json = atob(part.replace(/-/g, '+').replace(/_/g, '/'))
                const payload = JSON.parse(json)
                return Boolean(payload?.teacher_id || payload?.params?.teacher_id)
            } catch {
                return false
            }
        },
        visible() {
            if (this.isTeacherSession) return false
            if (this.isOnAllowedRoute) return false
            if (this.dismissedOnRoute === this.$route?.name) return false
            return this.isBlocked
        },
        isOnAllowedRoute() {
            const name = this.$route?.name
            return [
                'login',
                'worktime',
                'availability-teacher',
            ].includes(name)
        },
        rooms() {
            return this.blockingRooms
        },
        // "Alocação crítica há X dias" usando o maior days_in_red da lista.
        // Adiciona menção a slots quando há mais de 1 horário crítico
        // por sala (alinha com o que a tabela mostra).
        headlineText() {
            const maxDays = this.rooms.reduce((max, r) => Math.max(max, r.days_in_red || 0), 0)
            const slots = this.totalCriticalSlots || this.rooms.length
            const slotsExtra = (slots > this.rooms.length)
                ? ` (${slots} horários afetados)`
                : ''
            if (maxDays > 0) {
                return `Alocação crítica há ${maxDays} dia${maxDays > 1 ? 's' : ''}${slotsExtra}`
            }
            return `Sua unidade possui ambientes com superlotação crítica reincidente${slotsExtra}`
        },
        // Sem período/instituição selecionados, /vacancy-control rebota
        // pra /worktime via beforeEach guard. Mostrar isso no botão evita
        // a sensação de "click sem efeito".
        needsPeriodSelection() {
            return !this.hasAcesso
        },
        ctaLabel() {
            return 'Ir para Gestão de Vagas'
        },
        isOnVacancyControl() {
            return ['vacancy-control', 'ambiences-map'].includes(this.$route?.name)
        },
    },

    watch: {
        visible(open) {
            if (open) {
                this.$nextTick(() => {
                    this.$refs.overlay?.focus?.()
                })
            }
        },
        hasToken(now, before) {
            if (now && !before) {
                if (!this.isTeacherSession) this.ActionFetchBlockState()
            } else if (!now && before) {
                this.ActionClear()
            }
        },
        '$route.name'(now, before) {
            if (now && now !== before) {
                this.dismissedOnRoute = null
                if (this.hasToken && !this.isTeacherSession) this.ActionFetchBlockState()
            }
        },
    },

    mounted() {
        // Não consulta nem faz polling do block-state em sessão de professor.
        if (this.isTeacherSession) return
        this.ActionFetchBlockState()
        this.ActionStartPolling()
        // Reabre o overlay quando o usuário tenta acessar outro menu
        // enquanto bloqueado. O guard do router já redireciona para
        // /vacancy-control, mas se ele já estiver lá com o overlay
        // dispensado, sem este reset o painel não voltaria a aparecer.
        eventBus.$on('governance:blocked-nav-attempt', this.onBlockedNavAttempt)
    },

    beforeDestroy() {
        this.ActionStopPolling()
        eventBus.$off('governance:blocked-nav-attempt', this.onBlockedNavAttempt)
    },

    methods: {
        ...mapActions('governance', [
            'ActionFetchBlockState',
            'ActionStartPolling',
            'ActionStopPolling',
            'ActionSelectSuggestedContext',
            'ActionClear',
        ]),
        async goToVacancyControl() {
            // Sem acesso: tenta auto-selecionar o contexto sugerido pelo
            // backend (instituição + período + campi com as salas em
            // alerta) e força reload pra que o JWT atualize naturalmente.
            // Se a sugestão falhar, cai pro /worktime para escolha manual.
            if (this.needsPeriodSelection) {
                const ok = await this.ActionSelectSuggestedContext()
                if (ok) {
                    window.location.assign('/vacancy-control')
                    return
                }
                if (this.$route?.name !== 'worktime') {
                    this.$router.push({ name: 'worktime' }).catch(() => {})
                }
                return
            }
            if (this.$route?.path !== '/vacancy-control') {
                this.$router.push({ path: '/vacancy-control' }).catch(() => {})
                return
            }
            this.dismiss()
        },
        dismiss() {
            this.dismissedOnRoute = this.$route?.name || null
        },
        onBlockedNavAttempt() {
            this.dismissedOnRoute = null
            if (this.hasToken) this.ActionFetchBlockState()
            this.$nextTick(() => {
                this.$refs.overlay?.focus?.()
            })
        },
    },
}
</script>

<style scoped>
.governance-overlay {
    position: fixed;
    inset: 0;
    z-index: 9999;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 16px;
}
.governance-overlay__backdrop {
    position: absolute;
    inset: 0;
    background: rgba(15, 23, 42, 0.78);
    backdrop-filter: blur(2px);
}
.governance-overlay__panel {
    position: relative;
    background: #ffffff;
    border-radius: 12px;
    box-shadow: 0 24px 48px rgba(0, 0, 0, 0.32);
    padding: 32px 32px 24px;
    max-width: 560px;
    width: 100%;
    text-align: center;
    overflow: hidden;
}
.governance-overlay__topbar {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: #ef4444;
}
.governance-overlay__icon-wrap {
    position: relative;
    width: 80px;
    height: 80px;
    margin: 8px auto 12px;
}
.governance-overlay__shield {
    width: 80px;
    height: 80px;
    border-radius: 50%;
    background: #fef2f2;
    display: flex;
    align-items: center;
    justify-content: center;
    border: 1px solid #fecaca;
}
.governance-overlay__lock-badge {
    position: absolute;
    top: -2px;
    right: -2px;
    width: 26px;
    height: 26px;
    border-radius: 50%;
    background: #ef4444;
    display: flex;
    align-items: center;
    justify-content: center;
    border: 2px solid #ffffff;
    box-shadow: 0 2px 6px rgba(239, 68, 68, 0.4);
}
.governance-overlay__title {
    font-size: 22px;
    font-weight: 700;
    color: #1f2937;
    margin-bottom: 12px;
}
.governance-overlay__subtitle {
    font-size: 14px;
    color: #475569;
    line-height: 1.55;
    margin-bottom: 12px;
}
.governance-overlay__subtitle:last-of-type {
    margin-bottom: 20px;
    padding-bottom: 16px;
    border-bottom: 1px solid #e5e7eb;
}
.governance-overlay__headline {
    font-style: italic;
    font-size: 13px;
    color: #6b7280;
}
.governance-overlay__rooms {
    list-style: none;
    padding: 0;
    margin: 0 0 18px;
    text-align: left;
    max-height: 280px;
    overflow-y: auto;
}
.governance-overlay__room {
    display: flex;
    align-items: center;
    gap: 12px;
    border: 1px solid #fde2e2;
    background: #fef7f7;
    border-radius: 10px;
    padding: 12px 14px;
    margin-bottom: 8px;
}
.governance-overlay__room-icon {
    flex: 0 0 auto;
}
.governance-overlay__room-info {
    flex: 1 1 auto;
    min-width: 0;
}
.governance-overlay__room-name {
    font-weight: 600;
    color: #1f2937;
    font-size: 14px;
    line-height: 1.3;
}
.governance-overlay__room-meta {
    font-size: 12px;
    color: #64748b;
    margin-top: 2px;
}
.governance-overlay__room-chip {
    flex: 0 0 auto;
    font-size: 12px;
    font-weight: 600;
    padding: 4px 10px;
    border-radius: 999px;
    background: #ef4444;
    color: #ffffff;
    white-space: nowrap;
}
.governance-overlay__hint {
    display: flex;
    align-items: flex-start;
    gap: 4px;
    background: #f8fafc;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    padding: 10px 12px;
    font-size: 12.5px;
    color: #475569;
    line-height: 1.45;
    margin-bottom: 18px;
    text-align: left;
}
.governance-overlay__cta {
    text-transform: none !important;
    letter-spacing: 0 !important;
    font-weight: 600 !important;
}
.governance-overlay__dismiss {
    display: block;
    margin: 10px auto 0;
    text-transform: none !important;
    letter-spacing: 0 !important;
    color: #64748b !important;
}
.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.2s ease;
}
.fade-enter,
.fade-leave-to {
    opacity: 0;
}
</style>
